/**
 * Thin fetch wrappers around the admin HTTP surface (Module 6 +
 * 10-system-admin/01 + 10-system-admin/02). Mirrors `state/auth/api.ts` for envelope shape.
 *
 * Endpoints surfaced here:
 *   GET   /api/admin/whoami                          — Story 73 redirect signal
 *   GET   /api/admin/companies?cursor=&limit=        — Story 74 cursor list
 *   PATCH /api/admin/companies/:id/quota             — Story 77 quota adjust
 *   GET   /api/admin/attempts?...                    — Story 75 all attempts
 *   POST  /api/admin/attempts/:id/void               — Story 76 void attempt
 *   POST  /api/admin/impersonate/:company_id         — Story 76 start impersonation
 *   DELETE /api/admin/impersonate                    — Story 76 stop impersonation
 *   GET   /api/admin/audit-log?cursor=&limit=        — Story 82 audit log
 *
 * TanStack Query hooks deferred to Module 11.
 */

import { apiFetch } from '@/lib/api-client';

export type ApiEnvelope<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      error: { code: string; message: string; field?: string };
    };

export interface WhoamiPayload {
  is_system_admin: boolean;
  user: { id: string; email: string; name: string } | null;
  active_company: {
    id: string;
    name: string;
    role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  } | null;
}

export interface AdminCompanyListItem {
  id: string;
  name: string;
  contact_email: string;
  contact_name: string;
  quota_total: number;
  quota_used: number;
  status: 'active' | 'inactive';
  member_count: number;
  recent_attempt_count_30d: number;
  /** Tests assigned to this company via company_test_assignments. */
  tests_assigned_count: number;
  created_at: string;
}

export interface AdminCompaniesPage {
  items: AdminCompanyListItem[];
  next_cursor: string | null;
}

export interface AdminAttemptSummary {
  id: string;
  company_id: string;
  company_name: string;
  user_id: string;
  user_email: string;
  /** Human-readable display name (candidate_name if set, else email prefix). */
  display_name: string;
  test_id: string;
  test_title: string;
  test_track: string;
  test_level: string;
  status: 'in_progress' | 'completed' | 'voided';
  score_percentage: number | null;
  completed_at: string | null;
  created_at: string;
}

export interface AdminAttemptsPage {
  items: AdminAttemptSummary[];
  next_cursor: string | null;
}

export interface AdminAuditLogEntry {
  id: string;
  admin_user_id: string;
  action_type: string;
  target_resource_type: string;
  target_resource_id: string;
  reason: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface AdminAuditLogPage {
  items: AdminAuditLogEntry[];
  next_cursor: string | null;
}

// ─── 15-FG/Track-F payload shapes ───────────────────────────────────────────

export interface ShareTokenUsageEvent {
  id: string;
  referer: string | null;
  referer_host: string | null;
  ip_masked: string | null;
  allowed: boolean;
  loaded_at: string;
}

export interface ShareTokenUsagePayload {
  token: {
    id: string;
    label: string | null;
    test_id: string;
    company_id: string | null;
    is_embed_anonymous: boolean;
    allowed_embed_domains: string[];
    revoked_at: string | null;
  };
  summary: {
    last_seen_referer: string | null;
    distinct_referer_hosts_7d: number;
    total_loads_7d: number;
    embed_load_count_lifetime: number;
    out_of_allowlist_hosts: string[];
  };
  events: ShareTokenUsageEvent[];
}

export interface CompanyAssignmentItem {
  id: string;
  title: string;
  description: string;
  track: 'architecture' | 'mep' | 'structural' | 'interior' | null;
  level: 'novice' | 'intermediate' | 'advanced' | 'bim_champion' | null;
  status: 'draft' | 'active' | 'archived';
  estimated_minutes: number;
  author_name_at_publish: string | null;
  assigned: boolean;
  assigned_at: string | null;
  created_at: string;
}

export interface CompanyAssignmentsPayload {
  company: { id: string; name: string };
  items: CompanyAssignmentItem[];
  assigned_count: number;
  catalogue_count: number;
}

export interface BatchAssignmentResult {
  assigned: number;
  unassigned: number;
  unchanged_assign: number;
  unchanged_unassign: number;
}

export interface AvailabilityPatchPayload {
  id: string;
  availability_starts_at: string | null;
  availability_ends_at: string | null;
  availability_closed_reason: string | null;
}

async function getJson<T>(path: string): Promise<ApiEnvelope<T>> {
  const res = await apiFetch(path, {
    method: 'GET',
    credentials: 'same-origin',
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}

async function patchJson<T>(
  path: string,
  body: unknown,
): Promise<ApiEnvelope<T>> {
  const res = await apiFetch(path, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}

async function postJson<T>(
  path: string,
  body: unknown,
): Promise<ApiEnvelope<T>> {
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'same-origin',
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}

async function deleteJson<T>(path: string): Promise<ApiEnvelope<T>> {
  const res = await apiFetch(path, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  const text = await res.text();
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return {
      ok: false,
      error: {
        code: 'unexpected_response',
        message:
          'Something went wrong on our side. Please try again, or refresh the page if it keeps happening.',
      },
    };
  }
}

export interface AdminStats {
  questions: number;
  tests: number;
  attempts: number;
  /** 0..1 pass rate; null when there are 0 completed attempts. */
  pass_rate: number | null;
}

export const adminApi = {
  whoami: (): Promise<ApiEnvelope<WhoamiPayload>> =>
    getJson('/api/admin/whoami'),

  getStats: (): Promise<ApiEnvelope<AdminStats>> =>
    getJson('/api/admin/stats'),

  listCompanies: (opts?: {
    cursor?: string;
    limit?: number;
  }): Promise<ApiEnvelope<AdminCompaniesPage>> => {
    const q = new URLSearchParams();
    if (opts?.cursor) q.set('cursor', opts.cursor);
    if (opts?.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return getJson(`/api/admin/companies${qs ? `?${qs}` : ''}`);
  },

  adjustQuota: (
    companyId: string,
    body: { delta: number; reason: string },
  ): Promise<ApiEnvelope<{ quota_used: number }>> =>
    patchJson(`/api/admin/companies/${companyId}/quota`, body),

  listAttempts: (opts?: {
    cursor?: string;
    limit?: number;
    company_id?: string;
    test_id?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<ApiEnvelope<AdminAttemptsPage>> => {
    const q = new URLSearchParams();
    if (opts?.cursor) q.set('cursor', opts.cursor);
    if (opts?.limit) q.set('limit', String(opts.limit));
    if (opts?.company_id) q.set('company_id', opts.company_id);
    if (opts?.test_id) q.set('test_id', opts.test_id);
    if (opts?.status) q.set('status', opts.status);
    if (opts?.date_from) q.set('date_from', opts.date_from);
    if (opts?.date_to) q.set('date_to', opts.date_to);
    const qs = q.toString();
    return getJson(`/api/admin/attempts${qs ? `?${qs}` : ''}`);
  },

  voidAttempt: (
    attemptId: string,
    body: { reason: string },
  ): Promise<ApiEnvelope<{ status: string }>> =>
    postJson(`/api/admin/attempts/${attemptId}/void`, body),

  startImpersonation: (
    companyId: string,
    body: { reason?: string },
  ): Promise<ApiEnvelope<{ impersonating: boolean }>> =>
    postJson(`/api/admin/impersonate/${companyId}`, body),

  stopImpersonation: (): Promise<ApiEnvelope<{ impersonating: boolean }>> =>
    deleteJson('/api/admin/impersonate'),

  listAuditLog: (opts?: {
    cursor?: string;
    limit?: number;
  }): Promise<ApiEnvelope<AdminAuditLogPage>> => {
    const q = new URLSearchParams();
    if (opts?.cursor) q.set('cursor', opts.cursor);
    if (opts?.limit) q.set('limit', String(opts.limit));
    const qs = q.toString();
    return getJson(`/api/admin/audit-log${qs ? `?${qs}` : ''}`);
  },

  // ─── 15-FG/Track-F (J-A19, J-A20, J-X22) ──────────────────────────────────

  getShareTokenUsage: (
    tokenId: string,
  ): Promise<ApiEnvelope<ShareTokenUsagePayload>> =>
    getJson(`/api/admin/share-tokens/${tokenId}/usage`),

  listCompanyAssignments: (
    companyId: string,
    opts?: { include_status?: 'active' | 'draft' | 'archived' | 'all' },
  ): Promise<ApiEnvelope<CompanyAssignmentsPayload>> => {
    const q = new URLSearchParams();
    if (opts?.include_status) q.set('include_status', opts.include_status);
    const qs = q.toString();
    return getJson(
      `/api/admin/companies/${companyId}/assignments${qs ? `?${qs}` : ''}`,
    );
  },

  saveCompanyAssignments: (
    companyId: string,
    body: { assign?: string[]; unassign?: string[] },
  ): Promise<ApiEnvelope<BatchAssignmentResult>> =>
    postJson(`/api/admin/companies/${companyId}/assignments`, body),

  patchTestAvailability: (
    testId: string,
    body: {
      availability_starts_at?: string | null;
      availability_ends_at?: string | null;
      availability_closed_reason?: string | null;
    },
  ): Promise<ApiEnvelope<AvailabilityPatchPayload>> =>
    patchJson(`/api/admin/tests/${testId}/availability`, body),
};
